import React, { useReducer, useContext, useEffect, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	UserRoles,
	TLanguageIDs,
	IGetModifierGroupInfoV2,
	IGetModGroupRestaurantPropertiesArguments,
	IGetModGroupRestaurantProperties,
} from 'types';
import {
	viewModifierGroupInitialState,
	ViewModifierGroupContext,
	TTypeOfModalWindow,
} from './viewModifierGroupContext';
import { viewModifierGroupReducer } from './viewModifierGroupReducer';
import { ViewModifierGroupActionTypes } from './viewModifierGroupActions';
import { useNamedHeader } from 'hooks/useNamedHeader';
import { useFetchData } from 'hooks/useFetchData';
import { getModGroupRestaurantProperties } from 'backend/modifierGroups';
import { getModifierGroupInfoV2 } from 'backend/modifierGroups';
import { useRoleAccess } from 'hooks/useRoleAccess';

export const ViewModifierGroupState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(viewModifierGroupReducer, viewModifierGroupInitialState);
	const { showLoader, hideLoader, loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const params = new URL(document.location.href).searchParams;
	const modifierGroupID = params.get('modifierGroupID');

	const {
		response: modifierGroupFullInfoResponse,
		loading: modifierGroupFullInfoLoading,
		refetch: modifierGroupFullInfoRefetch,
	} = useFetchData<IGetModifierGroupInfoV2, string>(
		modifierGroupID ? getModifierGroupInfoV2 : null,
		modifierGroupID || '',
	);

	const {
		response: restaurantPropertiesResponse,
		loading: restaurantPropertiesLoading,
		refetch: restaurantPropertiesRefetch,
	} = useFetchData<IGetModGroupRestaurantProperties, IGetModGroupRestaurantPropertiesArguments>(
		state.restaurantIdForRestaurantProps && state.modifierGroupFullInfo?.id
			? getModGroupRestaurantProperties
			: null,
		{
			restaurantId: state.restaurantIdForRestaurantProps || '',
			modGroupId: state.modifierGroupFullInfo?.id || '',
		},
	);

	useNamedHeader(modifierGroupFullInfoResponse?.name);

	useEffect(() => {
		modifierGroupFullInfoLoading || restaurantPropertiesLoading ? showLoader() : hideLoader();
	}, [modifierGroupFullInfoLoading, restaurantPropertiesLoading]);

	useEffect(() => {
		if (modifierGroupFullInfoResponse) {
			const userRestaurantIDs = loggedUser.restaurants
				? loggedUser.restaurants.map((restaurant) => restaurant.id)
				: [];

			const accessibleRestaurants = hasAccess
				? modifierGroupFullInfoResponse.restaurants
				: modifierGroupFullInfoResponse.restaurants.filter((restaurant) =>
						userRestaurantIDs.includes(restaurant.id),
				  );

			dispatch({
				type: ViewModifierGroupActionTypes.SET_MODIFIER_GROUP_FULL_INFO,
				payload: {
					modifierGroupFullInfo: modifierGroupFullInfoResponse,
					accessibleRestaurants,
				},
			});
		}
	}, [loggedUser.restaurants, hasAccess, modifierGroupFullInfoResponse]);

	useEffect(() => {
		if (restaurantPropertiesResponse) {
			dispatch({
				type: ViewModifierGroupActionTypes.SET_RESTAURANT_PROPS,
				payload: {
					restaurantProps: restaurantPropertiesResponse,
				},
			});
		}
	}, [restaurantPropertiesResponse]);

	useEffect(() => {
		if (state.needRefetchData) {
			modifierGroupFullInfoRefetch();
			dispatch({
				type: ViewModifierGroupActionTypes.REFETCH_DATA,
				payload: {
					needRefetchData: false,
				},
			});
		}
	}, [state.needRefetchData]);

	useEffect(() => {
		restaurantPropertiesRefetch();
	}, [state.restaurantIdForRestaurantProps]);

	useEffect(() => {
		if (state.needRefetchRestPropsData) {
			restaurantPropertiesRefetch();
			dispatch({
				type: ViewModifierGroupActionTypes.REFETCH_REST_PROPS_DATA,
				payload: {
					needRefetchRestPropsData: false,
				},
			});
		}
	}, [state.needRefetchRestPropsData]);

	const refetchData = useCallback((value: boolean) => {
		dispatch({
			type: ViewModifierGroupActionTypes.REFETCH_DATA,
			payload: {
				needRefetchData: value,
			},
		});
	}, []);

	const refetchRestPropsData = useCallback((value: boolean) => {
		dispatch({
			type: ViewModifierGroupActionTypes.REFETCH_REST_PROPS_DATA,
			payload: {
				needRefetchRestPropsData: value,
			},
		});
	}, []);

	const setRestaurantIdForRestaurantProps = useCallback(
		(value: Nullable<string>) => {
			dispatch({
				type: ViewModifierGroupActionTypes.SET_RESTAURANT_ID_FOR_RESTAURANT_PROPS,
				payload: {
					restaurantIdForRestaurantProps: value,
					restaurantProps: value ? state.restaurantProps : null,
				},
			});
		},
		[state.restaurantProps],
	);

	const setTranslationTab = useCallback((value: Nullable<TLanguageIDs>) => {
		dispatch({
			type: ViewModifierGroupActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: ViewModifierGroupActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	return (
		<ViewModifierGroupContext.Provider
			value={{
				refetchData,
				refetchRestPropsData,
				setRestaurantIdForRestaurantProps,
				setTranslationTab,
				changeTypeOfModalWindow,
				needRefetchData: state.needRefetchData,
				needRefetchRestPropsData: state.needRefetchRestPropsData,
				modifierGroupFullInfo: state.modifierGroupFullInfo,
				accessibleRestaurants: state.accessibleRestaurants,
				restaurantIdForRestaurantProps: state.restaurantIdForRestaurantProps,
				restaurantProps: state.restaurantProps,
				selectedTranslationTab: state.selectedTranslationTab,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</ViewModifierGroupContext.Provider>
	);
};
